/* import __COLOCATED_TEMPLATE__ from './suggestion-details-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type { FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import moment from 'moment-timezone';
import type { Filter, LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type AppService from 'embercom/services/app-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingChartService from 'embercom/services/reporting-chart-service';

interface Signature {
  Args: {
    taskSuggestion: TaskSuggestion;
    suggestedConversationIds: string[];
    onConversationClicked: (conversation: ConversationExploreConversation) => void;
  };
}

export default class SuggestionDetailsPanel extends Component<Signature> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingChartService: ReportingChartService;
  @tracked loadedConversations: ConversationExploreConversation[] = [];
  @tracked isContextOpen = true;
  @tracked isConversationsOpen = true;
  @tracked isTopicsOpen = true;
  @tracked isMoreConversationsOpen = false;

  fetchConversationsTask = taskFor(this.loadKeyConversations);

  conversationData = trackedTask(this, this.fetchConversationsTask, () => [
    this.args.suggestedConversationIds,
  ]);

  get contextIcon() {
    return this.isContextOpen ? 'thin-down-arrow' : 'thin-right-arrow';
  }

  get conversationsIcon() {
    return this.isConversationsOpen ? 'thin-down-arrow' : 'thin-right-arrow';
  }

  get topicsIcon() {
    return this.isTopicsOpen ? 'thin-down-arrow' : 'thin-right-arrow';
  }

  get sampleConversationsSlice() {
    return this.loadedConversations.slice(0, 4);
  }

  get shouldSeeShowMoreConversations() {
    return this.loadedConversations.length > 4;
  }

  @action
  toggleMoreConversations(): void {
    this.isMoreConversationsOpen = !this.isMoreConversationsOpen;
  }

  @action
  toggleContext(): void {
    this.isContextOpen = !this.isContextOpen;
  }

  @action
  toggleConversations(): void {
    this.isConversationsOpen = !this.isConversationsOpen;
  }

  @action
  toggleTopics(): void {
    this.isTopicsOpen = !this.isTopicsOpen;
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  getLast12MonthsTimeRange(): FlexibleQueryTimeRange {
    let timezone = this.appService.app.timezone;
    let now = moment.tz(timezone);
    let start = now.clone().subtract(12, 'months').startOf('day').valueOf();
    let end = now.clone().endOf('day').valueOf();

    return {
      start,
      end,
      interval: 'week',
      property: this.metric.timeProperty,
    };
  }

  get attributeIds() {
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user_conversation_part_created_at',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
      'conversation_custom_fields#customer_experience_summary',
      'conversation_custom_fields#customer_experience_attributes',
    ];

    return attributeIds;
  }

  private buildFilterState(suggestedConversationIds: string[]): LogicalFilter {
    let filter: Filter[] = [
      {
        type: 'category',
        data: {
          attribute: 'conversation.id',
          property: 'conversation_id',
          values: suggestedConversationIds,
        },
      },
    ];

    return buildFiltersForDataConfig(
      null,
      this.metric,
      null,
      { type: 'and', filters: filter },
      false,
    ) as LogicalFilter;
  }

  @restartableTask
  *loadKeyConversations(): TaskGenerator<void> {
    if (!this.args.suggestedConversationIds?.length) {
      return;
    }

    let timeRange = this.getLast12MonthsTimeRange();
    let filterState = this.buildFilterState(this.args.suggestedConversationIds);

    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filterState,
      0,
      this.args.suggestedConversationIds.length,
      timeRange,
      this.attributeIds,
      'conversation.first_user_conversation_part_created_at', // sortBy
      'desc',
      this.reportingChartService.syntheticDataUseCase,
      this.appService.app.timezone,
      false,
    );

    this.loadedConversations = result.data.map((conversation: any) => ({
      id: Number(conversation['conversation.id']),
      userName: conversation['conversation.first_user.name'],
      userPseudonym: conversation['conversation.first_user.pseudonym'],
      topicSummary: conversation['conversation_custom_fields#ai_insights_conversation_summary'],
      subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
      topic: conversation['conversation_custom_fields#ai_insights_topic'],
      cxExplanation: this.appService.app.useNewCxSummary
        ? conversation['conversation_custom_fields#customer_experience_summary'] ||
          conversation['conversation.ai_generated_metrics.csat_explanation']
        : conversation['conversation.ai_generated_metrics.csat_explanation'],
      cxScore: conversation['conversation.ai_generated_metrics.csat'],
      cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
      time: conversation['conversation.first_user_conversation_part_created_at'],
    })) as ConversationExploreConversation[];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::SuggestionDetailsPanel': typeof SuggestionDetailsPanel;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/suggestion-details-panel': typeof SuggestionDetailsPanel;
  }
}
